const ignoreSelector = ':not([tabindex^="-"]):not([disabled])';
const focusableSelector =
    [
        'a[href]',
        'area[href]',
        'input',
        'select',
        'textarea',
        'button',
        'summary',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]',
    ].join(ignoreSelector + ',') + ignoreSelector;
const key = {
    tab: 9,
    escape: 27,
};

class Menu {
    constructor({ namespace, header }) {
        if (!namespace || !header) {
            return;
        }

        this.namespace = namespace;
        this.header = document.querySelector(header);

        if (!this.header) {
            return;
        }
        this.windowResizeTimeout = null;
        this.menuOpenClass = '-menuopen';
        this.secondLevelOpenClass = '-secondlevelopen';
        this.navigation = this.header.querySelector(`.${this.namespace}`);
        this.hamburger = this.header.querySelector(`.${this.namespace}__hamburger`);

        // Save all focusable into a variable
        // We need to filter the not visible out
        this.focusableRaw = [...this.header.querySelectorAll(focusableSelector)];
        this.focusable = [];

        this.firstLevelButtonItems = this.header.querySelectorAll(
            `.${this.namespace}__item--level-1.${this.namespace}__item--button`
        );
        this.firstLevelLinks = this.header.querySelectorAll(`.${this.namespace}__link--level-1`);
        this.secondLevelLinks = this.header.querySelectorAll(`.${this.namespace}__link--level-2`);
        this.backButtons = this.header.querySelectorAll(`.${this.namespace}__back`);
        this.closeButton = this.header.querySelector(`.${this.namespace}__close`);

        this.headerDimensions = this.header.querySelector('.header__dimensions');
        this.headerDimensionsLinks = this.header.querySelectorAll('.dimensions-menu__link');

        this.htmlElement = document.documentElement;
        this.bodyElement = document.body;

        this.isTouch = 'ontouchstart' in document.documentElement;
        this.isMobile = () => window.matchMedia('(max-width: 1199px)').matches;
        this.menuIsOpen = this.htmlElement.classList.contains(this.menuOpenClass);
        this.secondLevelOpen = this.htmlElement.classList.contains(this.secondLevelOpenClass);

        // save the event handler so it can be used in multiple places
        this.maintainFocusHandler = this.maintainFocus.bind(this);
        this.bindKeypressHandler = this.bindKeypress.bind(this);

        if (this.isMobile()) {
            this.firstLevelLinks.forEach((link) => {
                link.setAttribute('tabindex', '-1');
            });
        }

        this.setTabIndexForHeaderDimensionsLinks();

        this.setFocusable();
        this.addEvents();

        return {
            // boolean if the menu is on a touch device
            isTouch: this.isTouch,
            // returns a boolean if the mobile menu is open
            isOpen: () => {
                return this.menuIsOpen;
            },
            // open the mobile menu and return true.If it is already open or not visible it returns false
            open: this.openMenu.bind(this),
            // close the mobile menu and return true.If it is already closed it returns false
            close: this.closeMenu.bind(this),
            // toggle the menu(closes if open, open it if it is closed) return boolean status of the menu
            toggle: this.toggleMenu.bind(this),
        };
    }

    setFocusable() {
        this.focusable = this.filterFocusable(this.focusableRaw);
    }

    filterFocusable(arrayOfElements) {
        return arrayOfElements.filter((element) => this.checkVisibility(element));
    }

    checkVisibility(element) {
        let style = window.getComputedStyle(element);
        if (
            style.display == 'none' ||
            style.opacity == '0' ||
            style.visibility == 'hidden' ||
            element.offsetParent === null
        ) {
            return false;
        }

        return true;
    }

    addEvents() {
        window.addEventListener('resize', () => {
            if (this.windowResizeTimeout) {
                window.cancelAnimationFrame(this.windowResizeTimeout);
            }

            this.windowResizeTimeout = window.requestAnimationFrame(() => {
                this.setFocusable();
            });
        });

        this.hamburger.addEventListener('click', () => {
            this.toggleMenu();
            this.hamburger.blur();
        });

        this.closeButton.addEventListener('click', () => {
            this.closeMenu();
            this.closeButton.blur();
        });

        this.backButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this.resetMenu();
            });
        });

        this.firstLevelButtonItems.forEach((item) => {
            const buttonLink = item.querySelector(`.${this.namespace}__link--button`);

            if (!buttonLink) {
                return;
            }

            ['click', 'keydown'].forEach((eventType) =>
                buttonLink.addEventListener(eventType, (event) => {
                    if (event.type === 'keydown' && event.key !== 'Enter') {
                        return;
                    }

                    event.preventDefault();

                    this.openMenu();
                    this.resetMenu();

                    this.secondLevelOpen = true;
                    this.htmlElement.classList.add(this.secondLevelOpenClass);
                    this.bodyElement.classList.add(this.secondLevelOpenClass);

                    item.setAttribute('aria-expanded', 'true');
                    item.classList.add(`${this.namespace}__item--open`);

                    item.querySelectorAll(`.${this.namespace}__link--level-2`).forEach((link, index) => {
                        if (index === 0) {
                            link.focus();
                        }

                        link.setAttribute('tabindex', '0');
                    });

                    if (this.isMobile()) {
                        this.firstLevelLinks.forEach((link) => {
                            link.setAttribute('tabindex', '-1');
                        });
                        item.querySelectorAll(`.${this.namespace}__back`).forEach((backButton) => {
                            backButton.setAttribute('tabindex', '0');
                        });
                    } else {
                        buttonLink.setAttribute('tabindex', '-1');
                        this.closeButton.setAttribute('tabindex', '0');
                    }
                })
            );
        });

        this.headerDimensions.addEventListener('toggle', () => {
            this.setTabIndexForHeaderDimensionsLinks();
            this.setFocusable();
        });
    }

    openMenu() {
        if (this.menuIsOpen) {
            return false;
        }
        this.menuIsOpen = true;

        this.resetMenu();

        this.htmlElement.classList.add(this.menuOpenClass);
        this.bodyElement.classList.add(this.menuOpenClass);

        // open active second level on mobile
        const activeItemLink = this.header.querySelector(
            `.${this.namespace}__item--button.${this.namespace}__item--active .${this.namespace}__link--button`
        );
        if (this.isMobile() && activeItemLink) {
            setTimeout(() => {
                activeItemLink.click();
            }, 200);
        }

        // Add events
        this.bodyElement.addEventListener('focus', this.maintainFocusHandler);
        document.addEventListener('keydown', this.bindKeypressHandler);

        // Set focus on first element and blur it
        const firstFocusElement = this.focusable[0];
        if (firstFocusElement) {
            firstFocusElement.focus();
            firstFocusElement.blur();
        }

        this.triggerEvent('mobileMenu.open');

        return true;
    }

    closeMenu() {
        if (!this.menuIsOpen) {
            return false;
        }
        this.menuIsOpen = false;

        this.htmlElement.classList.remove(this.menuOpenClass);
        this.bodyElement.classList.remove(this.menuOpenClass);

        this.resetMenu();

        // Remove events
        try {
            this.bodyElement.removeEventListener('focus', this.maintainFocusHandler);
        } catch (error) {}
        try {
            document.removeEventListener('keydown', this.bindKeypressHandler);
        } catch (error) {}

        this.triggerEvent('mobileMenu.close');

        return true;
    }

    toggleMenu() {
        if (!this.menuIsOpen) {
            this.openMenu();
        } else {
            this.closeMenu();
        }
    }

    resetMenu() {
        this.secondLevelOpen = false;
        this.htmlElement.classList.remove(this.secondLevelOpenClass);
        this.bodyElement.classList.remove(this.secondLevelOpenClass);

        this.closeButton.setAttribute('tabindex', '-1');

        this.firstLevelButtonItems.forEach((item) => {
            item.setAttribute('aria-expanded', 'false');
            item.classList.remove(`${this.namespace}__item--open`);
        });

        this.firstLevelLinks.forEach((link) => {
            if (!this.isMobile() || (this.isMobile() && this.menuIsOpen)) {
                link.setAttribute('tabindex', '0');
            } else {
                link.setAttribute('tabindex', '-1');
            }
        });

        [...this.secondLevelLinks, ...this.backButtons].forEach((link) => {
            link.setAttribute('tabindex', '-1');
        });
    }

    triggerEvent(eventName) {
        let event;
        const options = {
            header: this.header,
            hamburger: this.hamburger,
            navigation: this.navigation,
            focusable: this.focusable,
        };
        if (window.CustomEvent) {
            event = new CustomEvent(eventName, { detail: options });
        } else {
            event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventName, true, true, options);
        }
        document.dispatchEvent(event);
    }

    maintainFocus(event) {
        // If the menu is shown and the focus is not within the header,
        // move it back to its first focusable child
        if (this.menuIsOpen && !this.header.contains(event.target) && this.focusable.length) {
            this.focusable[0].focus();
        }
    }

    bindKeypress(event) {
        if (this.menuIsOpen) {
            // If ESCAPE key is being pressed, prevent any further
            // effects from the ESCAPE key and hide the Menu

            if (event.which === key.escape) {
                event.preventDefault();
                this.closeMenu();
            }

            // If TAB key is being pressed, make sure the
            // focus stays trapped within the menu
            if (event.which === key.tab) {
                this.trapTabKey(event);
            }
        }
    }

    trapTabKey(event) {
        let index = this.focusable.indexOf(document.activeElement);

        // If the SHIFT key is being pressed while tabbing (moving backwards) and
        // the currently focused item is the first one, move the focus to the last
        // focusable item from the menu
        if (event.shiftKey && index === 0) {
            this.focusable[this.focusable.length - 1].focus();
            event.preventDefault();

            // If the SHIFT key is not being pressed (moving forwards) and the
            // currently focused item is the last one, move the focus to the
            // first focusable item from the menu
        } else if (!event.shiftKey && index === this.focusable.length - 1) {
            this.focusable[0].focus();
            event.preventDefault();
        }
    }

    setTabIndexForHeaderDimensionsLinks() {
        this.headerDimensionsLinks.forEach((link) => {
            link.setAttribute('tabindex', this.headerDimensions && this.headerDimensions.open ? '0' : '-1');
        });
    }
}

export default Menu;
